import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  BarChart3,
  BadgeCheck,
  Building2,
  ClipboardList,
  FileBadge,
  Flag,
  FolderTree,
  Gauge,
  Layers,
  LayoutDashboard,
  Library,
  ListChecks,
  ShieldAlert,
  ShieldCheck,
  User,
  Users2,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@/lib/auth-store';
import { isPlatformUser } from '@/types/auth';
import type { AuthenticatedUser } from '@/types/auth';
import { cn } from '@/lib/utils';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

export function AppSidebar(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const sections = buildSections(t, user);

  return (
    <nav aria-label="Primary" className="hidden w-60 flex-shrink-0 border-e bg-white p-3 md:block">
      {sections.map((section, idx) => (
        <div key={idx} className={cn(idx > 0 && 'mt-4')}>
          {section.title ? (
            <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {section.title}
            </div>
          ) : null}
          <ul className="space-y-1">
            {section.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/app'}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100',
                    )
                  }
                >
                  <item.icon className="h-4 w-4" aria-hidden="true" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="mt-6 rounded-md bg-slate-50 p-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-2 font-medium text-slate-700">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{user?.userType}</span>
        </div>
        {(user?.roleKeys ?? []).length > 0 ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {(user?.roleKeys ?? []).map((k) => (
              <span key={k} className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-700">
                {k}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </nav>
  );
}

/**
 * Phase 11f: navigation gating now reads the role-key set from the
 * authenticated user (provided by the backend's auth response) so a
 * Department Manager doesn't see admin entries even when their
 * userType is TENANT_STAFF, and an Executive Viewer (readOnly) sees
 * only read-class surfaces.
 *
 * The backend still enforces every check — this is a UX prune, not
 * a security boundary.
 */
export function buildSections(
  t: (k: string) => string,
  user: AuthenticatedUser | null | undefined,
): NavSection[] {
  // Dashboard link points at /app for managers/admins (their roles
  // have a true KPI dashboard). For Compliance Officer / Department
  // Staff, the "dashboard" is their personal self-profile view at
  // /app/me — same shape that their manager sees when drilling into
  // them, so they get the gauge + KPI tiles + recent tasks/obs/evidence
  // tables instead of a generic admin board. The "مهامي" tab remains
  // in the GRC section for the focused task queue.
  const roleKeysEarly = user?.roleKeys ?? [];
  const isExecutiveViewer = roleKeysEarly.includes('EXECUTIVE_VIEWER');
  const homeIsMe =
    roleKeysEarly.includes('COMPLIANCE_OFFICER') ||
    roleKeysEarly.includes('DEPARTMENT_STAFF');
  const sections: NavSection[] = isExecutiveViewer
    ? [{
        items: [
          { to: '/app/executive', label: t('nav.executive'), icon: BarChart3 },
          { to: '/app/risks', label: t('nav.risks'), icon: ShieldAlert },
        ],
      }]
    : homeIsMe
      ? [{ items: [{ to: '/app/me', label: t('nav.my_dashboard'), icon: LayoutDashboard }] }]
      : [{ items: [{ to: '/app', label: t('nav.dashboard'), icon: LayoutDashboard }] }];
  if (!user) {
    sections.push({
      title: t('nav.section_account'),
      items: [{ to: '/app/profile', label: t('nav.profile'), icon: User }],
    });
    return sections;
  }

  if (isExecutiveViewer) {
    return sections;
  }

  if (isPlatformUser(user)) {
    sections.push({
      title: t('nav.section_platform'),
      items: [
        { to: '/app/super-admin/tenants', label: t('nav.tenants'), icon: Building2 },
        { to: '/app/super-admin/license', label: t('nav.license'), icon: FileBadge },
      ],
    });
    sections.push({
      title: t('nav.section_account'),
      items: [{ to: '/app/profile', label: t('nav.profile'), icon: User }],
    });
    return sections;
  }

  const roleKeys = user.roleKeys ?? [];
  const hasRole = (...keys: string[]) => keys.some((k) => roleKeys.includes(k));
  // Admin gate is defence-in-depth: TRUST the userType for system
  // admins even when their roleKeys are temporarily empty (e.g. a
  // pre-existing tenant before the role catalogue was seeded). The
  // backend still policy-gates every action, so this only widens
  // the menu — never API access.
  const isSystemAdminUserType =
    user.userType === 'TENANT_SYSTEM_ADMIN' || user.userType === 'TENANT_ADMIN';
  const isAdminRole = hasRole('TENANT_ADMIN') || isSystemAdminUserType;
  const isManagerRole = user.isManager || hasRole('COMPLIANCE_MANAGER', 'RISK_MANAGER', 'DEPARTMENT_MANAGER');
  // Tenant-wide managers (Compliance + Risk) own cross-tenant
  // surfaces like the Executive view. Department Manager is
  // intentionally excluded — they only hold `dashboards:department`,
  // not `dashboards:executive`, so showing them the Executive link
  // would surface a 403 on click.
  const isTenantWideManager = hasRole('COMPLIANCE_MANAGER', 'RISK_MANAGER');
  const isExecutiveOrAuditor = hasRole('EXECUTIVE_VIEWER', 'AUDITOR_REVIEWER');
  const isStaff = hasRole('DEPARTMENT_STAFF');
  const isComplianceManager = hasRole('COMPLIANCE_MANAGER');
  const isComplianceOfficer = hasRole('COMPLIANCE_OFFICER');
  const isRiskManager = hasRole('RISK_MANAGER');
  const isDeptManager = hasRole('DEPARTMENT_MANAGER');

  if (isRiskManager && !isAdminRole) {
    sections.push({
      title: t('nav.section_risk'),
      items: [
        { to: '/app/risks', label: t('nav.risks'), icon: ShieldAlert },
        { to: '/app/kris', label: t('nav.kris'), icon: Gauge },
        { to: '/app/incidents', label: t('nav.incidents'), icon: AlertTriangle },
        { to: '/app/risk-appetite', label: t('nav.risk_appetite'), icon: BadgeCheck },
        { to: '/app/manager/team', label: t('nav.team'), icon: Users2 },
      ],
    });
    sections.push({
      title: t('nav.section_account'),
      items: [{ to: '/app/profile', label: t('nav.profile'), icon: User }],
    });
    return sections;
  }

  // Department Manager — strictly supervisory role per workflow 14.
  // Spec: "لا يشوف: أطر تنظيمية | أرقام ضوابط | أدلة تفصيلية | خطة سنوية".
  // Their entire surface is the home page (DepartmentManagerHome at /app)
  // which carries the KPI card, escalations from compliance manager, and
  // staff cards + per-staff task table. No GRC sidebar entries at all.
  // Return early with just Dashboard + Profile so nothing else slips in.
  if (isDeptManager && !isAdminRole) {
    sections.push({
      title: t('nav.section_account'),
      items: [{ to: '/app/profile', label: t('nav.profile'), icon: User }],
    });
    return sections;
  }

  // GRC section — strictly for compliance/risk operators. TENANT_ADMIN
  // is the platform-administration role: their job is overseeing the
  // tenant (users, departments, integrations, settings) and the
  // Dashboard already aggregates GRC KPIs for monitoring. The
  // operational GRC pages (frameworks, assessments, observations,
  // incidents, reports, executive view) belong to the Compliance
  // Manager / Officer / Auditor / Executive Viewer roles, not the
  // admin. Same rule as Tasks above.
  //
  // Officer vs Manager split (customer ask:
  // "موظف الامتثال المفترض ما يشوف الا مهامه هو فقط و ردود
  //  الإدارات عليه وليس كل ما يتعلق بإدارة الالتزام كاملة"):
  //   - Compliance Manager → full GRC catalogue (orchestrates the
  //     pipeline across departments).
  //   - Compliance Officer → focused queue: Dashboard + My Tasks +
  //     Approvals + Profile. Officer doesn't browse the
  //     framework/assessment/observation/report surfaces — they
  //     work the items routed to them and review department
  //     responses to those items inside the Tasks queue.
  const grcItems: NavItem[] = [];
  if (!isAdminRole) {
    // Compliance Manager — consolidated entries. Six operational
    // surfaces (Tasks, Assessments, Observations, Assignment, Annual
    // Plan, Escalations) collapse into one "مهامي" tab shell at
    // /app/work. Frameworks + Escalation Policy + Profile collapse
    // into /app/settings (handled at the bottom of this function).
    // The rest of the GRC items (Team, Executive, Departments view)
    // remain standalone because they're navigation hubs, not work
    // surfaces.
    if (isComplianceManager) {
      grcItems.push({ to: '/app/work', label: t('nav.my_work'), icon: ClipboardList });
      // CM → Admin change-request form lives inside Settings as a
      // tab now (/app/settings/change-requests). The standalone
      // sidebar entry is removed — CM reaches it via Settings.
    }
    // Frameworks + Assessments — managers + auditors. Executive Viewer
    // returned early above with only Executive + Risk Register.
    // Officer + Compliance Manager intentionally excluded (officer by
    // role design; manager because Frameworks lives under Settings
    // and Assessments is a tab under Work).
    if ((isManagerRole && !isComplianceManager) || isExecutiveOrAuditor) {
      grcItems.push({ to: '/app/frameworks', label: t('nav.frameworks'), icon: Library });
      grcItems.push({ to: '/app/assessments', label: t('nav.assessments'), icon: ListChecks });
    }
    // Observations — managers + auditors. Compliance Manager reaches
    // this from the /app/work shell so we skip the standalone entry.
    if ((isManagerRole && !isComplianceManager) || isExecutiveOrAuditor) {
      grcItems.push({ to: '/app/observations', label: t('nav.observations'), icon: AlertTriangle });
    }
    // Reports — compliance strategic surface; risk has its own register.
    if ((isManagerRole && !isRiskManager) || isExecutiveOrAuditor) {
      grcItems.push({ to: '/app/reports', label: t('nav.reports'), icon: BarChart3 });
    }
    // Executive view — only roles with dashboards:executive permission.
    if (isTenantWideManager || isExecutiveOrAuditor) {
      grcItems.push({ to: '/app/executive', label: t('nav.executive'), icon: BarChart3 });
    }
    if (isManagerRole) {
      grcItems.push({ to: '/app/manager/team', label: t('nav.team'), icon: Users2 });
    }
    // Departments tabbed view (WF16a/d) — Compliance Manager browses
    // each department's dashboard via /app/departments-view.
    if (isComplianceManager) {
      grcItems.push({
        to: '/app/departments-view',
        label: t('nav.departments_view'),
        icon: Building2,
      });
    }
    // Tasks — the Officer's + Staff's primary surface. Compliance
    // Manager skips this — they reach Tasks via the /app/work shell.
    if (isStaff || isComplianceOfficer) {
      grcItems.push({
        to: '/app/workspace/tasks',
        label: isStaff ? t('nav.my_requests') : t('nav.tasks'),
        icon: ClipboardList,
      });
    }
    // Escalations inbox — Compliance Officer keeps the standalone
    // link (they need a quick way in). Manager-side roles other than
    // Compliance Manager keep it too. Compliance Manager reaches this
    // from /app/work.
    if ((isManagerRole && !isComplianceManager) || isComplianceOfficer) {
      grcItems.push({ to: '/app/escalations', label: t('nav.escalations'), icon: Flag });
    }
    // Assignment matrix — manager-only. Compliance Manager skips
    // (lives under /app/work). Tenant Admin DROPPED from this entry
    // per the 2026-06-02 sketch ("قلص عدد الروابط"): admin oversees
    // platform infra, not day-to-day routing.
    if (isManagerRole && !isComplianceManager) {
      grcItems.push({ to: '/app/assignment', label: t('nav.assignment'), icon: ClipboardList });
    }
    // Annual Plan — Tenant Admin entry DROPPED for the same reason.
    // Compliance Manager reaches it from /app/work.
  }
  if (grcItems.length > 0) {
    // DEPARTMENT_STAFF should not see "GRC" / "الحوكمة والامتثال"
    // anywhere in their UI — they're a dept user, not a compliance
    // operator. Their items live under "عملي" / "My work".
    sections.push({
      title: isStaff ? t('nav.section_my_work') : t('nav.section_grc'),
      items: grcItems,
    });
  }

  // Tenant Admin sidebar — collapsed to the 5 operational hubs the
  // admin actually owns day-to-day. Spec source: hand-drawn sketch
  // dated 2026-06-02 ("القائمة العلوية... قلص عدد الروابط").
  // The dropped admin pages (SSO, SCIM, Webhooks, Branding,
  // Framework Catalogue, Escalation Policy, Audit Integrity) remain
  // routable and surface as TABS inside /app/settings — no
  // functionality lost, just consolidated visual surface.
  if (isAdminRole) {
    sections.push({
      title: t('nav.section_admin'),
      items: [
        { to: '/app/tenant-admin/users', label: t('nav.users'), icon: Users2 },
        { to: '/app/departments', label: t('nav.departments'), icon: FolderTree },
        { to: '/app/admin/framework-catalogue', label: t('nav.frameworks'), icon: Layers },
        { to: '/app/approvals', label: t('nav.approvals'), icon: BadgeCheck },
      ],
    });
  }
  // Approvals (Maker-Checker) — intentionally Tenant-Admin-only in
  // the sidebar (TENANT_ADMIN already gets it via section_admin
  // above). The engine currently guards two break-glass actions —
  // tenant.disable_mfa and role.elevate_privileges — both of which
  // are TENANT_ADMIN's own surface, so no other role generates or
  // reviews requests in the current code. The page is preserved (and
  // routing stays open via /app/approvals) so we can wire more
  // actions later, but operational sidebars stay clean.

  // Settings (Frameworks + Escalation Policy + Profile) are reached
  // from the gear button in the top nav, not the sidebar. Non-manager
  // roles still get a Profile entry.
  if (!isComplianceManager && !isAdminRole) {
    sections.push({
      title: t('nav.section_account'),
      items: [{ to: '/app/profile', label: t('nav.profile'), icon: User }],
    });
  }

  return sections;
}
